import type { FullComposeViewState } from 'owa-calendar-compose-types';
import isNewEvent from '../../selectors/isNewEvent';
import shouldShowTimeSuggestions from '../../selectors/shouldShowTimeSuggestions';
import {
    getComposeViewState,
    getMetricsInfo,
    getOpenedForm,
    getComposeEvent,
    setMetricsInfo,
    setOpenedForm,
    setViewState,
} from 'owa-calendar-compose-store-manager';
import createComposeViewState from '../../utils/createComposeViewState';
import initFreeBusyCache from '../../utils/initFreeBusyCache';
import { clearPendingInlineImages } from '../../utils/pendingInlineImages';
import { onShowFullCompose } from '../formOpenCloseNotificationActions';
import { isConnectedAccount } from 'owa-accounts-store';
import { wrapFunctionForDatapoint } from 'owa-analytics';
import { lazyInitializeCalendarChannelPicker } from 'owa-calendar-channel-picker';
import { initializeLocationMetrics } from '../../mutators/eventMetricsMutators';
import { isEventAMeeting } from 'owa-calendar-event-capabilities';
import {
    FullComposeFormViewType,
    FULL_COMPOSE_FORM_TYPE,
    QUICK_COMPOSE_FORM_TYPE,
} from 'owa-calendar-helpers-types/lib/ComposeTypes';
import initAttachmentWell from 'owa-calendar-meeting-forms-common-actions/lib/actions/initAttachmentWell';
import {
    convertToSchedulingSelectionItem,
    initializeSchedulingGrid,
} from 'owa-calendar-scheduling-grid';
import { KnownItemTypes } from 'owa-calendar-surface-dategrid';
import { initializeTimeSuggestions, setCalendarComponent } from 'owa-calendar-time-suggestions';
import { startOfDay } from 'owa-datetime';
import initializeLocationFeedbackManagerState from 'owa-feedback-manager-actions/lib/actions/initializeLocationFeedbackManagerState';
import entityFeedbackStore from 'owa-feedback-manager-store/lib/store/locationFeedbackManagerStore';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import { getOpxHostApi } from 'owa-opx';
import { lazyInitializeCache } from 'owa-recipient-cache';
import { lazyShowRoomPanel } from 'owa-room-selection';
import { isConsumer } from 'owa-session-store';
import {
    FullComposeEntrySourceType,
    FULL_COMPOSE_ENTRY_SOURCE_QUICK_COMPOSE,
} from 'owa-calendar-helpers-types/lib/ComposeMetricTypes';
import {
    logComposeFormOpenedTimes,
    initializeTimeSuggestionMetricsInfo,
    verifyEventThenSave,
    presentDiscardClosureConfirmation,
    openComposeSchedulingAssistant,
} from 'owa-calendar-compose-form-actions';
import getCalendarComponent from '../../utils/getCalendarComponent';
import type { CreateCollabSpaceOnInitProps } from 'owa-calendar-helpers-types';
import createFullComposeMetricsInfo from '../../utils/createFullComposeMetricsInfo';

/**
 * Shows the full compose form by initializng the full compose form
 * @param {boolean} isCharmExplicitlySet If true, charm suggestions will not be provided until charm is unset.
 * @param {FullComposeEntrySourceType} entrySource
 * @param {extractionSourceId} A specific entity extraction id associates with the action source
 * @param {internetMessageId} An unique id for a message
 */
export default wrapFunctionForDatapoint(
    {
        name: 'showFullCompose',
        customData: (
            isCharmExplicitlySet: boolean,
            entrySource: FullComposeEntrySourceType,
            viewType: FullComposeFormViewType,
            shouldOpenSchedulingAssistant?: boolean,
            extractionSourceId?: string | null,
            internetMessageId?: string,
            createCollabSpaceOnInitProps?: CreateCollabSpaceOnInitProps,
            formId?: string
        ) => {
            return {
                isCharmExplicitlySet: isCharmExplicitlySet,
                entrySource: entrySource,
                viewType: viewType,
            };
        },
    },
    function showFullCompose(
        isCharmExplicitlySet: boolean,
        entrySource: FullComposeEntrySourceType,
        viewType: FullComposeFormViewType,
        shouldOpenSchedulingAssistant?: boolean,
        extractionSourceId?: string | null,
        internetMessageId?: string,
        createCollabSpaceOnInitProps?: CreateCollabSpaceOnInitProps,
        formId?: string
    ) {
        const composeFormId = formId ?? null;

        // Only valid when opening full compose from quick compose
        const collabSpaceViewStateId = getComposeViewState(composeFormId)?.collabSpaceViewStateId;

        const viewState: FullComposeViewState = createComposeViewState(
            getComposeEvent(composeFormId),
            isCharmExplicitlySet,
            viewType,
            isAcceptOrReviewPNTEntrySource(entrySource) /* isAcceptPNT */,
            extractionSourceId,
            internetMessageId,
            collabSpaceViewStateId,
            createCollabSpaceOnInitProps,
            formId
        );

        return internalShowFullCompose(
            composeFormId,
            viewState,
            entrySource,
            extractionSourceId,
            shouldOpenSchedulingAssistant
        );
    }
);

export async function internalShowFullCompose(
    formId: string,
    viewState: FullComposeViewState,
    entrySource: FullComposeEntrySourceType,
    extractionSourceId?: string | null,
    shouldOpenSchedulingAssistant?: boolean
) {
    const event = getComposeEvent(formId);

    setMetricsInfo(
        formId,
        createFullComposeMetricsInfo(getMetricsInfo(formId), event, entrySource, extractionSourceId)
    );

    logComposeFormOpenedTimes(formId);

    if (isHostAppFeatureEnabled('composeFormSaveCloseHandlers')) {
        // if opx scenario, then register save and close handlers
        const opxHostApi = getOpxHostApi();
        if (opxHostApi.registerFormSaveHandler) {
            opxHostApi.registerFormSaveHandler(() => verifyEventThenSave(formId));
        }

        if (opxHostApi.registerFormCloseHandler) {
            opxHostApi.registerFormCloseHandler(() =>
                presentDiscardClosureConfirmation(formId, undefined /* targetWindow */)
            );
        }
    }

    // Inline image plugin requires the attachment well to be initialized
    await initAttachmentWell(event, false /* reInitialize */, viewState, false /* isReadOnly */);

    // Clear pending inline images added by previous compose session if any
    // TODO: Update to be per-form https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/119230
    clearPendingInlineImages();

    setViewState(formId, viewState);

    // This must occur after initAttachmentWell.  See https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/121503
    const previousOpenedForm = getOpenedForm(formId);
    setOpenedForm(formId, FULL_COMPOSE_FORM_TYPE);

    // initialize entityFeedbackStore if needed
    if (!entityFeedbackStore.feedbackManagerState) {
        initializeLocationFeedbackManagerState();
    }

    // initialize FreeBusy Cache if needed
    initFreeBusyCache(formId);

    initializeSchedulingGrid.importAndExecute(
        viewState.schedulingGridScenarioId,
        [event.ParentFolderId.Id],
        startOfDay(event.Start),
        convertToSchedulingSelectionItem(event, KnownItemTypes.suggestedItems),
        true, // showSelectionItem
        true //disableTabbableSurfaceItems
    );

    if (shouldShowTimeSuggestions(formId)) {
        if (previousOpenedForm != QUICK_COMPOSE_FORM_TYPE) {
            // If the suggestions flight is enabled, only then import the suggestions code
            initializeTimeSuggestions.importAndExecute(
                event,
                viewState.shouldAdjustStartTimeEndTime,
                getCalendarComponent(formId) /* calendarComponent */,
                formId
            );
            initializeTimeSuggestionMetricsInfo(formId);
        } else {
            setCalendarComponent(getCalendarComponent(formId), formId);
        }
    }

    const userIdentity = event.ParentFolderId.mailboxInfo.userIdentity;
    if (event.ParentFolderId.mailboxInfo) {
        lazyShowRoomPanel.importAndExecute(
            event,
            isConsumer(userIdentity),
            false /** isDeepLink */,
            formId
        );
    }

    // if this is not a transition from quick compose then
    // initialize the location metrics and people cache
    if (entrySource != FULL_COMPOSE_ENTRY_SOURCE_QUICK_COMPOSE) {
        initializeLocationMetrics(formId);
        if (!isConnectedAccount(userIdentity)) {
            lazyInitializeCache.importAndExecute();
        }
    }

    lazyInitializeCalendarChannelPicker.importAndExecute(event, formId);

    if (shouldOpenSchedulingAssistant) {
        openComposeSchedulingAssistant(formId);
    }

    // Notify OPX listeners that full compose is ready, must be second to last call in this method
    if (isHostAppFeatureEnabled('navigateToView')) {
        // After form is open, then let the host know that the view has changed to ComposeForm
        const opxHostApi = getOpxHostApi();
        if (opxHostApi.navigateToView) {
            opxHostApi.navigateToView(
                isNewEvent(formId) ? 'ComposeForm' : 'EditForm',
                isEventAMeeting(event) ? 'Send' : 'Save',
                event
            );
        }
    }

    // Notify listeners that full compose is ready, must be last call in this method
    onShowFullCompose(formId, viewState.viewType);
}

function isAcceptOrReviewPNTEntrySource(entrySource: FullComposeEntrySourceType) {
    return (
        entrySource == 'Mail_AcceptPNT' ||
        entrySource == 'PeekReviewPNT' ||
        entrySource == 'TimePanelReviewPNT' ||
        entrySource == 'ReviewPNT'
    );
}
